import Anthropic from '@anthropic-ai/sdk';


const anthropic = new Anthropic({
  apiKey: import.meta.env.VITE_ANTHROPIC_API_KEY,
  dangerouslyAllowBrowser: true,
});

function getErrorMessage(statusCode, errorData) {
  if (statusCode === 401) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your Anthropic API key.' };
  } else if (statusCode === 403) {
    return { isInternal: true, message: 'Permission denied. Your API key does not have access to the specified resource.' };
  } else if (statusCode === 404) {
    return { isInternal: true, message: 'Resource not found. The requested endpoint or model may not exist.' };
  } else if (statusCode === 429) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (statusCode === 500) {
    return { isInternal: true, message: 'Anthropic service error. An unexpected error occurred on their servers. Please try again later.' };
  } else if (statusCode === 529) {
    return { isInternal: true, message: 'Anthropic service is temporarily overloaded. Please try again in a few moments.' };
  } else {
    return { isInternal: false, message: errorData?.error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const claudeComplianceService = {
  /**
   * Advanced compliance analysis using Claude Sonnet 4.5
   * Provides deeper regulatory interpretation and contextual analysis
   */
  async analyzeComplianceWithContext(payrollData, regulatoryContext = {}) {
    try {
      const prompt = `You are an expert in PNG Employment Act compliance and payroll regulations. Analyze the following payroll data with deep contextual understanding:

Payroll Data:
${JSON.stringify(payrollData, null, 2)}

Regulatory Context:
- PNG Minimum Wage: K4.50/hour
- Overtime Rate: 1.5x for regular overtime, 2x for public holidays/Sundays
- NASFUND: 8.4% employer contribution, 6% employee contribution
- SWT (Salary and Wages Tax): Progressive tax rates
- Annual Leave: 3 weeks minimum
- Sick Leave: 2 weeks minimum
- Public Holiday Pay: Double time for work on public holidays

Additional Context:
${JSON.stringify(regulatoryContext, null, 2)}

Provide a comprehensive analysis including:
1. Detailed compliance violations with specific regulation references
2. Contextual interpretation of regulations as they apply to this specific case
3. Risk assessment (legal, financial, reputational)
4. Comparative analysis against industry standards
5. Proactive recommendations to prevent future violations
6. Regulatory trend analysis and upcoming changes to be aware of`;

      const response = await anthropic?.messages?.create({
        model: 'claude-sonnet-4-5-20250929',
        max_tokens: 4000,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      return {
        analysis: response?.content?.[0]?.text,
        model: 'claude-sonnet-4-5',
        timestamp: new Date()?.toISOString(),
        usage: {
          inputTokens: response?.usage?.input_tokens,
          outputTokens: response?.usage?.output_tokens
        }
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error?.status, error);
      const err = new Error(errorInfo.message);
      err.statusCode = error?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Claude compliance analysis error:', err);
      }
      throw err;
    }
  },

  /**
   * Intelligent violation pattern detection using Claude's advanced reasoning
   * Identifies complex patterns and systemic issues
   */
  async detectViolationPatterns(historicalViolations, employeeData = []) {
    try {
      const prompt = `You are a compliance pattern detection expert. Analyze the following historical violation data to identify intelligent patterns, root causes, and systemic issues:

Historical Violations (${historicalViolations?.length} records):
${JSON.stringify(historicalViolations?.slice(0, 50), null, 2)}

Employee Context (${employeeData?.length} employees):
${JSON.stringify(employeeData?.slice(0, 20), null, 2)}

Perform advanced pattern detection:

1. **Temporal Patterns**: Identify time-based patterns (monthly, quarterly, seasonal)
2. **Departmental Patterns**: Detect department-specific compliance issues
3. **Employee Type Patterns**: Analyze patterns by employment type (full-time, part-time, casual)
4. **Violation Clustering**: Group related violations that indicate systemic problems
5. **Root Cause Analysis**: Identify underlying causes beyond surface-level violations
6. **Predictive Indicators**: Detect early warning signs of future violations
7. **Correlation Analysis**: Find relationships between different violation types
8. **Cost Impact Patterns**: Identify patterns with highest financial impact

Provide structured output with:
- Pattern name and description
- Frequency and trend direction
- Affected entities (departments, employees, violation types)
- Root causes and contributing factors
- Risk level (critical, high, medium, low)
- Recommended prevention strategies
- Estimated cost impact if unaddressed`;

      const response = await anthropic?.messages?.create({
        model: 'claude-sonnet-4-5-20250929',
        max_tokens: 4000,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      return {
        patterns: response?.content?.[0]?.text,
        model: 'claude-sonnet-4-5',
        timestamp: new Date()?.toISOString(),
        analysisDepth: 'advanced',
        usage: {
          inputTokens: response?.usage?.input_tokens,
          outputTokens: response?.usage?.output_tokens
        }
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error?.status, error);
      const err = new Error(errorInfo.message);
      err.statusCode = error?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Claude pattern detection error:', err);
      }
      throw err;
    }
  },

  /**
   * Government regulation interpretation using Claude's deep reasoning
   * Translates complex legal language into actionable guidance
   */
  async interpretRegulation(regulationText, applicationContext = {}) {
    try {
      const prompt = `You are a PNG Employment Act legal expert. Interpret the following government regulation and provide clear, actionable guidance:

Regulation Text:
${regulationText}

Application Context:
${JSON.stringify(applicationContext, null, 2)}

Provide comprehensive interpretation:

1. **Plain Language Summary**: Explain the regulation in simple, clear terms
2. **Key Requirements**: List specific compliance requirements
3. **Practical Application**: How to apply this regulation in real payroll scenarios
4. **Common Misinterpretations**: Clarify frequent misunderstandings
5. **Edge Cases**: Address unusual situations and exceptions
6. **Implementation Steps**: Step-by-step guide to ensure compliance
7. **Documentation Requirements**: What records must be maintained
8. **Penalties for Non-Compliance**: Consequences of violations
9. **Recent Updates**: Any recent changes or amendments
10. **Related Regulations**: Other regulations that interact with this one

Format the response for easy understanding by HR and payroll professionals.`;

      const response = await anthropic?.messages?.create({
        model: 'claude-sonnet-4-5-20250929',
        max_tokens: 3000,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      return {
        interpretation: response?.content?.[0]?.text,
        model: 'claude-sonnet-4-5',
        timestamp: new Date()?.toISOString(),
        regulationSource: regulationText?.substring(0, 100) + '...',
        usage: {
          inputTokens: response?.usage?.input_tokens,
          outputTokens: response?.usage?.output_tokens
        }
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error?.status, error);
      const err = new Error(errorInfo.message);
      err.statusCode = error?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Claude regulation interpretation error:', err);
      }
      throw err;
    }
  },

  /**
   * Advanced remediation strategy generation using Claude Opus 4.5
   * Provides comprehensive, context-aware remediation plans
   */
  async generateAdvancedRemediation(violations, organizationalContext = {}) {
    try {
      const violationsList = violations?.map(v => 
        `- ${v?.violationType || v?.violation_type}: ${v?.description} (Severity: ${v?.severity}, Affected: ${v?.affectedCount || v?.affected_count || 0} employees, Cost: K${v?.estimatedCost || v?.estimated_cost || 0})`
      )?.join('\n');

      const prompt = `You are a PNG Employment Act compliance strategist. Generate comprehensive remediation strategies for the following violations:

Violations:
${violationsList}

Organizational Context:
${JSON.stringify(organizationalContext, null, 2)}

For each violation, provide:

1. **Immediate Actions** (within 24-48 hours):
   - Critical steps to stop ongoing violations
   - Emergency communications required
   - Temporary measures to mitigate risk

2. **Short-Term Remediation** (1-2 weeks):
   - Detailed correction procedures
   - Employee communications and training
   - System/process adjustments
   - Documentation updates

3. **Long-Term Prevention** (1-3 months):
   - Systemic changes to prevent recurrence
   - Policy updates and implementations
   - Automated controls and validations
   - Ongoing monitoring procedures

4. **Stakeholder Management**:
   - Internal communications (employees, management)
   - External communications (government, auditors)
   - Change management approach

5. **Resource Requirements**:
   - Personnel needed
   - Budget estimates
   - Technology/tools required
   - Timeline and milestones

6. **Success Metrics**:
   - KPIs to track remediation progress
   - Compliance verification methods
   - Risk reduction measurements

7. **Regulatory Compliance**:
   - Required government notifications
   - Audit trail documentation
   - Legal considerations

Provide actionable, prioritized strategies that balance speed, cost, and effectiveness.`;

      const response = await anthropic?.messages?.create({
        model: 'claude-opus-4-5-20251101',
        max_tokens: 8000,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      return {
        remediationPlan: response?.content?.[0]?.text,
        model: 'claude-opus-4-5',
        timestamp: new Date()?.toISOString(),
        violationCount: violations?.length,
        usage: {
          inputTokens: response?.usage?.input_tokens,
          outputTokens: response?.usage?.output_tokens
        }
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error?.status, error);
      const err = new Error(errorInfo.message);
      err.statusCode = error?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Claude remediation generation error:', err);
      }
      throw err;
    }
  },

  /**
   * Comparative compliance analysis
   * Compares current compliance status against industry benchmarks
   */
  async compareAgainstBenchmarks(complianceData, industryType = 'general') {
    try {
      const prompt = `You are a PNG compliance benchmarking expert. Analyze the following compliance data and compare it against industry standards:

Current Compliance Data:
${JSON.stringify(complianceData, null, 2)}

Industry Type: ${industryType}

Provide comparative analysis:

1. **Industry Benchmarks**: Standard compliance metrics for ${industryType} sector in PNG
2. **Performance Comparison**: How this organization compares to industry averages
3. **Best Practices**: What top-performing organizations are doing differently
4. **Gap Analysis**: Specific areas where improvement is needed
5. **Competitive Positioning**: Compliance as a competitive advantage
6. **Risk Ranking**: How compliance risks compare to industry norms
7. **Improvement Roadmap**: Prioritized steps to reach industry-leading compliance

Provide data-driven insights with specific metrics and actionable recommendations.`;

      const response = await anthropic?.messages?.create({
        model: 'claude-sonnet-4-5-20250929',
        max_tokens: 3000,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      return {
        benchmarkAnalysis: response?.content?.[0]?.text,
        model: 'claude-sonnet-4-5',
        timestamp: new Date()?.toISOString(),
        industryType,
        usage: {
          inputTokens: response?.usage?.input_tokens,
          outputTokens: response?.usage?.output_tokens
        }
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error?.status, error);
      const err = new Error(errorInfo.message);
      err.statusCode = error?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Claude benchmark analysis error:', err);
      }
      throw err;
    }
  },

  /**
   * Predictive compliance forecasting using Claude's reasoning
   * Forecasts future compliance risks based on current trends
   */
  async forecastComplianceRisks(historicalData, timeframe = '90 days') {
    try {
      const prompt = `You are a predictive compliance analyst. Analyze historical compliance data and forecast future risks:

Historical Data:
${JSON.stringify(historicalData, null, 2)}

Forecast Timeframe: ${timeframe}

Provide predictive analysis:

1. **Trend Analysis**: Current compliance trends and trajectories
2. **Risk Forecast**: Predicted violations and compliance issues in the next ${timeframe}
3. **Probability Assessment**: Likelihood of each predicted risk (high/medium/low with percentages)
4. **Impact Estimation**: Potential financial and operational impact
5. **Early Warning Indicators**: Signals to watch for emerging risks
6. **Seasonal Factors**: Time-based patterns that may affect compliance
7. **Preventive Actions**: Proactive steps to avoid predicted violations
8. **Resource Planning**: Anticipated resource needs for compliance management
9. **Regulatory Changes**: Upcoming regulation changes that may impact compliance
10. **Confidence Levels**: How confident the predictions are based on data quality

Provide actionable forecasts that enable proactive compliance management.`;

      const response = await anthropic?.messages?.create({
        model: 'claude-sonnet-4-5-20250929',
        max_tokens: 3500,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      return {
        forecast: response?.content?.[0]?.text,
        model: 'claude-sonnet-4-5',
        timestamp: new Date()?.toISOString(),
        timeframe,
        usage: {
          inputTokens: response?.usage?.input_tokens,
          outputTokens: response?.usage?.output_tokens
        }
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error?.status, error);
      const err = new Error(errorInfo.message);
      err.statusCode = error?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Claude forecast error:', err);
      }
      throw err;
    }
  },

  /**
   * Multi-jurisdiction compliance analysis
   * Analyzes compliance across multiple regulatory frameworks
   */
  async analyzeMultiJurisdiction(payrollData, jurisdictions = ['PNG']) {
    try {
      const prompt = `You are an international compliance expert. Analyze payroll data for compliance across multiple jurisdictions:

Payroll Data:
${JSON.stringify(payrollData, null, 2)}

Jurisdictions: ${jurisdictions?.join(', ')}

Provide multi-jurisdiction analysis:

1. **Jurisdiction-Specific Requirements**: Key compliance requirements for each jurisdiction
2. **Cross-Jurisdiction Conflicts**: Areas where regulations conflict or overlap
3. **Harmonization Opportunities**: Where compliance can be streamlined across jurisdictions
4. **Jurisdiction-Specific Violations**: Violations unique to each jurisdiction
5. **Compliance Complexity Score**: Overall complexity rating for multi-jurisdiction compliance
6. **Recommended Approach**: Strategy for managing multi-jurisdiction compliance
7. **Documentation Requirements**: Records needed for each jurisdiction
8. **Reporting Obligations**: Government reporting requirements by jurisdiction

Provide clear guidance for managing compliance across multiple regulatory frameworks.`;

      const response = await anthropic?.messages?.create({
        model: 'claude-sonnet-4-5-20250929',
        max_tokens: 3500,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      return {
        multiJurisdictionAnalysis: response?.content?.[0]?.text,
        model: 'claude-sonnet-4-5',
        timestamp: new Date()?.toISOString(),
        jurisdictions,
        usage: {
          inputTokens: response?.usage?.input_tokens,
          outputTokens: response?.usage?.output_tokens
        }
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error?.status, error);
      const err = new Error(errorInfo.message);
      err.statusCode = error?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Claude multi-jurisdiction analysis error:', err);
      }
      throw err;
    }
  }
};

export default claudeComplianceService;